import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User } from 'lucide-react';
import Button from '../ui/button';
import { supabase } from '../../lib/supabase';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  image_url: string | null;
  author: string;
  category: string;
  slug: string;
  created_at: string;
}

interface ArticlesSectionProps {
  title?: string;
  subtitle?: string;
}

export default function ArticlesSection({
  title = "🧠 Nos dernières ressources pour",
  subtitle = "Méthodes, outils, bonnes pratiques : on partage ce qui marque sur le terrain pour recruter mieux, intégrer plus vite, et engager durablement vos équipes.",
}: ArticlesSectionProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const { data, error } = await supabase
          .from('articles')
          .select('*')
          .eq('published', true)
          .order('created_at', { ascending: false })
          .limit(6);

        if (error) {
          console.warn('Error fetching articles, using fallback data:', error);
          // Don't return early, let it fall through to set empty array
        }

        setArticles(data || []);
      } catch (error) {
        console.warn('Network error fetching articles, using fallback data:', error);
        setArticles([]);
      }
      setIsLoading(false);
    };

    fetchArticles();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.165, 0.84, 0.44, 1] },
    },
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getCategoryColor = (category?: string) => {
    const colors: { [key: string]: string } = {
      'Recrutement': 'bg-blue-100 text-blue-800',
      'Marque employeur': 'bg-pink-100 text-pink-800',
      'RH & Management': 'bg-purple-100 text-purple-800',
      'Onboarding': 'bg-green-100 text-green-800',
      'Culture d\'entreprise': 'bg-yellow-100 text-yellow-800',
      'Innovation RH': 'bg-indigo-100 text-indigo-800',
      'Général': 'bg-neutral-100 text-neutral-800'
    };
    return colors[category || 'Général'] || colors['Général'];
  };

  return (
    <section className="py-20">
      <div className="container-custom">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl font-bold mb-6">
            {title}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
              penser RH autrement
            </span>
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-neutral-200 aspect-[16/9] rounded-t-xl" />
                <div className="bg-white p-6 rounded-b-xl">
                  <div className="h-6 bg-neutral-200 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-neutral-200 rounded w-full mb-2" />
                  <div className="h-4 bg-neutral-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {articles.map((article) => (
              <motion.article
                key={article.id}
                variants={itemVariants}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-200 group cursor-pointer"
                onClick={() => window.location.href = `/ressources/articles/${article.slug || article.id}`}
              >
                {article.image_url && (
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                    />
                    {article.category && (
                      <div className="absolute top-4 left-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(article.category)}`}>
                          {article.category}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  
                  <p className="text-sm text-neutral-600 mb-4 line-clamp-3">
                    {article.excerpt || article.content}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm text-neutral-500">
                    {article.author && (
                      <div className="flex items-center gap-1">
                        <User size={16} />
                        <span>{article.author}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      <span>{formatDate(article.created_at)}</span>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        )}

        <div className="text-center mt-12">
          <Button
            variant="outline"
            size="lg"
            onClick={() => window.location.href = '/ressources/articles'}
          >
            📚 Voir tous nos articles
          </Button>
        </div>
      </div>
    </section>
  );
}